import { ExerciseResponse } from '@src/data/exercise-response'
import ApiClient from '@src/lib/api_client'
import { ExerciseResults, GraphAverageResults } from '@src/data/workout-results'
import { API } from '@src/lib/api_path'
import camelcaseKeys from 'camelcase-keys'
import SessionManager from '@src/lib/session_manager'
import { ExerciseTagFilter } from '@src/state/store'
import { NO_EQUIPMENT } from '@src/util/constants'

type ExercisesResponse = { exercises: ExerciseResponse[] }

/**
 * Service utility dealing with exercises.
 */
export class ExerciseService {
  /**
   * Check if given exercise matches given filters
   *
   * @param exercise - exercise to be filtered
   * @param filters - filters used to find if exercise matches the criteria
   */
  static filterByTagsAndEquipment = (exercise: ExerciseResponse, filters: ExerciseTagFilter) => {
    for (let type in filters) {
      // Ignore health limitation tags when filtering exercises
      if (type === 'health') continue

      const selectedTags: Set<string> = (filters as any)[type] || new Set<string>()
      let keyFound = true
      if (selectedTags.size > 0) {
        keyFound = false
        const exerciseTagKeys = exercise.getTagKeysWOHealthLimitations()
        selectedTags.forEach((rawTag) => {
          let tag = (rawTag || '').toLowerCase()
          if (tag === NO_EQUIPMENT && !exercise.equipment.length) keyFound = true
          else if (exerciseTagKeys.includes(tag) || exercise.equipment.includes(tag)) keyFound = true
        })
        if (!keyFound) return false
      }
    }
    return true
  }

  /**
   * Check if given exercise matches given name
   *
   * @param exercise - exercise to be filtered
   * @param filterName - string used to find if exercise name matches
   */
  static filterByName = (exercise: ExerciseResponse, filterName: string) => {
    if (!filterName || !filterName.trim().length) {
      return true
    }
    return exercise.getName().toLowerCase().includes(filterName.trim().toLowerCase())
  }

  /**
   * Check if given exercise matches given id
   *
   * @param exercise - exercise to be filtered
   * @param filterId - string used to find if exercise id matches
   */
  static filterByNumber = (exercise: ExerciseResponse, filterId: string) => {
    if (!filterId || !filterId.trim().length) {
      return true
    }
    return exercise.number.toString().startsWith(filterId.trim())
  }

  /**
   * Check if exercise is in favorites list
   *
   * @param exercise - exercise to be filtered
   * @param favoritesOnly - whether to filter only favorites
   * @param favoriteIds - set of favorite exercise IDs
   */
  static filterByFavorites = (exercise: ExerciseResponse, favoritesOnly: boolean, favoriteIds: Set<number>) => {
    if (!favoritesOnly) {
      return true
    }
    // Prefer isFavorite from exercise object (from backend) if available, otherwise check set
    if (exercise && exercise.isFavorite !== undefined && exercise.isFavorite !== null) {
      return exercise.isFavorite
    }
    return favoriteIds.has(exercise.id)
  }

  getExerciseGraphData (memberId, exerciseId) {
    let apiClient = new ApiClient()
    return apiClient.get<GraphAverageResults>(`${ (SessionManager.Instance.isManager() ? API.TRAINERS_API.EXERCISE_AVERAGE_STATS : API.MEMBERS_API.EXERCISE_AVERAGE_STATS)
      .replace('{memberId}', memberId)
      .replace('{exerciseId}', exerciseId)}`)
      .then((response: GraphAverageResults) => {
        return Promise.resolve(new GraphAverageResults(camelcaseKeys(response, { deep: true })))
      })
      .catch(error => {
        console.error('failed to retrieve exercises', error)
        return Promise.reject(error)
      })
  }

  getExerciseResults (memberId, exerciseId) {
    let apiClient = new ApiClient()
    return apiClient.get<ExerciseResults>(`${(SessionManager.Instance.isManager() ? API.TRAINERS_API.EXERCISE_RESULTS : API.MEMBERS_API.EXERCISE_RESULTS)
      .replace('{memberId}', memberId)
      .replace('{exerciseId}', exerciseId)}`)
      .then((response: ExerciseResults) => {
        return Promise.resolve(new ExerciseResults(camelcaseKeys(response, { deep: true })))
      })
      .catch(error => {
        console.error('failed to retrieve exercises', error)
        return Promise.reject(error)
      })
  }

  // load both station dependent and home exercises
  loadAllExercises () {
    let apiClient = new ApiClient(SessionManager.Instance.isManager() ? API.TRAINERS_API.EXERCISES : API.MEMBERS_API.EXERCISES)
    return apiClient.get<ExercisesResponse>('?include_home_exercises=true')
      .then((response) => {
        const exercises = response.exercises.map((attrs) => new ExerciseResponse(camelcaseKeys(attrs)))
        return Promise.resolve(exercises)
      })
      .catch((error) => {
        console.error('failed to retrieve exercises', error)
      })
  }
}
